import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/conversation-copies`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const statuses = { APPROVED: 'Aprobada', PENDING: 'En revisión de Meta', REJECTED: 'Rechazada por Meta',
  NOT_FOUND: 'Pendiente de solicitar', NOT_CONFIGURED: 'Falta el ID de la cuenta WhatsApp Business',
  LOOKUP_FAILED: 'No se pudo consultar en Meta', CONTENT_MISMATCH: 'La plantilla existente tiene otro contenido',
  PAUSED: 'Pausada por Meta', DISABLED: 'Deshabilitada por Meta' };

export default function ConversationCopies() {
  const [data, setData] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [phone, setPhone] = useState('');
  const [waba, setWaba] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const load = async (populate = false) => {
    const res = await axios.get(API, auth());
    setData(res.data);
    if (populate) { setEnabled(res.data.enabled); setPhone(res.data.phone); setWaba(res.data.business_account_id); }
    return res.data;
  };
  useEffect(() => {
    let active = true;
    axios.get(API, auth()).then(res => {
      if (!active) return;
      setData(res.data); setEnabled(res.data.enabled); setPhone(res.data.phone); setWaba(res.data.business_account_id);
    }).catch(() => { if (active) setError('No se pudo cargar la configuración de copias.'); });
    return () => { active = false; };
  }, []);
  const run = async (action) => {
    setBusy(true); setError(''); setNotice('');
    try { await action(); }
    catch (e) { const detail = e.response?.data?.detail; setError(typeof detail === 'string' ? detail : detail?.detail || detail?.message || 'No se pudo completar la operación.'); }
    finally { setBusy(false); }
  };
  const save = () => run(async () => {
    await axios.post(API, { enabled, phone, business_account_id: waba }, auth());
    await load(true); setNotice('Destino guardado. Las copias nuevas se conservan pendientes hasta que la plantilla esté aprobada.');
  });
  const prepare = () => run(async () => {
    const res = await axios.post(`${API}/prepare-template`, {}, auth());
    await load(); setNotice(`Plantilla: ${statuses[res.data.status] || res.data.status}.`);
  });
  const test = () => run(async () => {
    await axios.post(`${API}/test`, {}, auth());
    await load(); setNotice('Prueba en cola para el celular guardado. Actualiza para comprobar la entrega.');
  });
  const counts = data?.counts || {};
  return <Card className="bg-white/5 border-white/10">
    <CardHeader><CardTitle className="text-white">Copias de conversaciones a mi WhatsApp</CardTitle></CardHeader>
    <CardContent className="space-y-4">
      <p className="text-sm text-gray-300">Recibe los textos registrados de pacientes y las respuestas de Gina en WhatsApp, Instagram, Facebook y el chat web. Incluye comentarios públicos. Los textos largos se dividen en partes; los archivos se indican para consultar el original en Aimedplanner.</p>
      <label className="flex items-center gap-2 text-white"><input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} disabled={busy || !data} /> Activar copias completas</label>
      <div><Label htmlFor="conversation-copy-phone">Mi celular para recibir copias</Label><Input id="conversation-copy-phone" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+52 55 1234 5678" disabled={busy || !data} /></div>
      <div><Label htmlFor="conversation-copy-waba">ID de cuenta WhatsApp Business</Label><Input id="conversation-copy-waba" value={waba} onChange={e => setWaba(e.target.value)} disabled={busy || !data} /></div>
      <p className="text-sm text-gray-300">Las copias usan una plantilla aprobada para funcionar también fuera de la ventana de 24 horas. Meta puede cobrar por cada parte entregada. La suscripción comienza al activarla; no reenvía el historial anterior.</p>
      <p className="text-sm text-gray-300">Plantilla: <strong>{statuses[data?.template?.status] || data?.template?.status || 'Consultando'}</strong></p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={save} disabled={busy || !data}>Guardar destino de copias</Button>
        <Button onClick={prepare} disabled={busy || !data?.business_account_id}>Solicitar plantilla de copias</Button>
        <Button onClick={test} disabled={busy || !data?.enabled || data?.template?.status !== 'APPROVED'}>Enviar prueba a mi celular</Button>
        <Button onClick={() => run(() => load())} disabled={busy}>Actualizar entrega</Button>
      </div>
      <p className="text-sm text-gray-300">Pendientes: {counts.pending || 0} · Aceptadas: {counts.accepted || 0} · Enviadas: {counts.sent || 0} · Entregadas: {(counts.delivered || 0) + (counts.read || 0)} · Fallidas: {counts.failed || 0} · Sin confirmar: {(counts.unknown || 0) + (counts.submitting || 0)}</p>
      {!!data?.recent?.length && <ul className="text-xs text-gray-300 space-y-1">{data.recent.map((row, i) => <li key={`${row.reference}-${row.part}-${i}`}>{row.reference} ({row.part}/{row.parts}): {row.status}{row.error_code ? ` — error ${row.error_code}` : ''}{row.errors?.[0]?.code ? ` — error ${row.errors[0].code}` : ''}</li>)}</ul>}
      {notice && <p role="status" className="text-sm text-green-300">{notice}</p>}
      {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
    </CardContent>
  </Card>;
}
